/**
 * Agents API Client
 * Endpoints para gestión de agentes
 */

import { apiClient } from './client';
import type { Agent, AgentMetrics, AgentPolicy, AgentCommand } from '@/types/agent';

export const agentsApi = {
  claimAgent: async (agentId: string, organizationId: string, password: string): Promise<void> => {
    await apiClient.post('/agents/claim', { agentId, organizationId, password });
  },

  createClaimCredential: async (
    agentId: string,
    password?: string
  ): Promise<{ password: string }> => {
    const response = await apiClient.post<{ password: string }>('/agents/claim-credential', {
      agentId,
      password,
    });
    return response.data;
  },
  /**
   * Get all agents
   */
  getAgents: async (): Promise<Agent[]> => {
    const response = await apiClient.get<Agent[]>('/agents');
    return response.data;
  },

  /** Authoritative REST snapshot; revision prevents a pre-delete request
   * finishing late from resurrecting an agent in another tab. */
  getAgentsSnapshot: async (): Promise<{ agents: Agent[]; lifecycleRevision?: number }> => {
    const response = await apiClient.get<Agent[]>('/agents');
    const rawRevision = response.headers['x-lifecycle-revision'];
    const lifecycleRevision = typeof rawRevision === 'string' ? Number(rawRevision) : undefined;
    return {
      agents: response.data,
      lifecycleRevision: Number.isSafeInteger(lifecycleRevision) ? lifecycleRevision : undefined,
    };
  },

  /**
   * Get single agent by ID
   */
  getAgent: async (agentId: string): Promise<Agent> => {
    const response = await apiClient.get<Agent>(`/agents/${agentId}`);
    return response.data;
  },

  /**
   * Get agent metrics
   */
  getAgentMetrics: async (agentId: string): Promise<AgentMetrics> => {
    const response = await apiClient.get<AgentMetrics>(`/agents/${agentId}/metrics`);
    return response.data;
  },

  /**
   * Get agent policy
   */
  getAgentPolicy: async (agentId: string): Promise<AgentPolicy> => {
    const response = await apiClient.get<AgentPolicy>(`/admin/agents/${agentId}/policy`);
    return response.data;
  },

  /**
   * Update agent policy
   */
  updateAgentPolicy: async (agentId: string, policy: AgentPolicy): Promise<void> => {
    await apiClient.post(`/admin/agents/${agentId}/policy`, policy);
  },

  /**
   * Update agent hostname
   */
  updateAgentHostname: async (agentId: string, hostname: string): Promise<void> => {
    await apiClient.post(`/admin/agents/${agentId}/meta`, { hostname });
  },

  /**
   * Send command to agent
   */
  sendCommand: async (agentId: string, command: AgentCommand): Promise<void> => {
    await apiClient.post(`/agents/${agentId}/commands`, command);
  },

  /**
   * Delete agent
   */
  deleteAgent: async (agentId: string): Promise<number | undefined> => {
    const response = await apiClient.delete(`/agents/${agentId}`);
    const rawRevision = response.headers['x-lifecycle-revision'];
    const lifecycleRevision = typeof rawRevision === 'string' ? Number(rawRevision) : undefined;
    return Number.isSafeInteger(lifecycleRevision) ? lifecycleRevision : undefined;
  },
};
